import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Avatar, Box, Center, Separator, Flex, Heading, Spinner, Stack, VStack } from '@chakra-ui/react';
import { useEducationContext } from '@src/components/sco/Education/EducationContext';
import { EducationErrorState } from '@src/components/sco/Education/EducationErrorState';
import { useVideoSize } from '@src/components/sco/Education/Layout/VideoToolbar/useVideoSize';
import { TrackedVideoPlayer } from '@src/components/sco/Education/TrackedVideoPlayer';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { UserAccessGate } from '@src/components/sco/UserAccessGate';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { Video } from '@src/graphql-types';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
type VideoLessonDetailProps = {
  videoListSlug?: string;
  videoSlug?: string;
};
export const VideoLessonDetail = props => {
  const $ = _c(59);
  const {
    maxWidth
  } = useVideoSize();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const educationContext = useEducationContext();
  const params = useSearchParams();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = VideoLessonDetail.queries.videoList();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.videoListSlug) {
    t1 = {
      variables: {
        videoListSlug: props.videoListSlug
      }
    };
    $[1] = props.videoListSlug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const videoListQuery = useQuery(t0, t1);
  let t2;
  if ($[3] !== props.videoSlug || $[4] !== videoListQuery.data) {
    t2 = videoListQuery.data?.videoList?.videos ? videoListQuery.data.videoList.videos.find(video => video.slug === props.videoSlug)?.videoLesson : null;
    $[3] = props.videoSlug;
    $[4] = videoListQuery.data;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [activeVideo, setActiveVideo] = useState(t2);
  let t3;
  if ($[6] !== props.videoSlug || $[7] !== videoListQuery.data) {
    t3 = videoListQuery.data?.videoList?.steps ? videoListQuery.data.videoList.videos.findIndex(video_0 => video_0.slug === props.videoSlug)?.videoLesson : null;
    $[6] = props.videoSlug;
    $[7] = videoListQuery.data;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const [activeVideoIndex, setActiveVideoIndex] = useState(t3);
  const videoList = videoListQuery.data?.videoList;
  let t4;
  if ($[9] !== props.videoSlug || $[10] !== videoList) {
    t4 = () => {
      if (videoList) {
        const videoLesson = videoList.videos.find(video_1 => video_1.slug === props.videoSlug);
        const videoLessonIndex = videoList.videos.findIndex(video_2 => video_2.slug === props.videoSlug);
        setActiveVideo(videoLesson);
        setActiveVideoIndex(videoLessonIndex);
      }
    };
    $[9] = props.videoSlug;
    $[10] = videoList;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = videoList?.id;
  let t6;
  if ($[12] !== props.videoSlug || $[13] !== t5) {
    t6 = [t5, props.videoSlug];
    $[12] = props.videoSlug;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useEffect(t4, t6);
  let t7;
  if ($[15] !== activeVideo || $[16] !== educationContext || $[17] !== videoList?.slug || $[18] !== videoList?.title) {
    t7 = () => {
      if (activeVideo) {
        educationContext.setActiveContentItem(activeVideo);
        educationContext.setBreadcrumbs([{
          title: "Videos",
          href: "/videos"
        }, {
          title: videoList?.title,
          href: `/videos/${videoList?.slug}`
        }, {
          title: activeVideo?.title,
          href: `/videos/${videoList?.slug}/${activeVideo?.slug}`
        }]);
      }
    };
    $[15] = activeVideo;
    $[16] = educationContext;
    $[17] = videoList?.slug;
    $[18] = videoList?.title;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = videoList?.slug;
  const t9 = videoList?.title;
  const t10 = activeVideo?.id;
  let t11;
  if ($[20] !== t10 || $[21] !== t8 || $[22] !== t9) {
    t11 = [t8, t9, t10];
    $[20] = t10;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useUniversalEffect(t7, t11);
  if (videoList && activeVideo) {
    let t12;
    if ($[24] !== params) {
      t12 = params?.get("play");
      $[24] = params;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    const autoPlay = parseInt(t12) === 1;
    let t13;
    if ($[26] !== activeVideo || $[27] !== activeVideoIndex || $[28] !== autoPlay || $[29] !== getBreakpointValue || $[30] !== maxWidth) {
      t13 = activeVideo && <UserAccessGate bypass={activeVideoIndex === 0} ctaVariant="video-overlay" require={{
        isRegistered: true
      }} ctaProps={{
        bgImageUrl: activeVideo.featuredImage?.url,
        boxProps: {
          maxWidth: "30em"
        },
        emailCaptureProps: {},
        subtitle: "Create a free account to unlock this video.",
        title: "Register to continue watching.",
        wrapperProps: {
          ...getBreakpointValue({
            base: {
              minHeight: "60vh"
            },
            md: {
              aspectRatio: 1.7777777777777777
            }
          }),
          maxWidth
        }
      }}><UserAccessGate ctaVariant="video-overlay" bypass={activeVideoIndex === 0 || {
          contentPermissions: ["canAccessVideos"],
          viewLimit: {
            viewableType: "VideoLesson",
            viewableId: activeVideo.id,
            minimumCount: 1,
            minimumCompletionAsPercent: 80
          }
        }} ctaProps={{
          bgImageUrl: activeVideo.featuredImage?.url,
          boxProps: {
            width: "auto"
          },
          title: "Upgrade to continue watching.",
          subtitle: "Unlock all our videos, courses, and more with a Basic Membership.",
          wrapperProps: {
            ...getBreakpointValue({
              base: {
                minHeight: "60vh"
              },
              md: {
                aspectRatio: 1.7777777777777777
              }
            }),
            maxWidth
          }
        }} require={{
          viewLimit: {
            viewableType: "VideoLesson",
            maximumCount: 10,
            minimumCompletionAsPercent: 80
          }
        }}><Flex maxWidth={maxWidth} transition="max-width 0.2s ease-in-out" width="full"><TrackedVideoPlayer key={activeVideo.videoUrl} autoPlay={autoPlay} borderRadius="0" imageUrl={activeVideo.featuredImage?.url} videoUrl={activeVideo.videoUrl} viewableId={activeVideo.id} viewableType={activeVideo.__typename} /></Flex></UserAccessGate></UserAccessGate>;
      $[26] = activeVideo;
      $[27] = activeVideoIndex;
      $[28] = autoPlay;
      $[29] = getBreakpointValue;
      $[30] = maxWidth;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== t13) {
      t14 = <Flex bgColor="black" justify="center" width="full"><VStack gap={0} width="full">{t13}</VStack></Flex>;
      $[32] = t13;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== getBreakpointValue) {
      t15 = getBreakpointValue([10, 10, 20]);
      $[34] = getBreakpointValue;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    if ($[36] !== activeVideo?.people) {
      t16 = activeVideo?.people && activeVideo?.people.map((expert, index) => {
        const isLast = index === activeVideo?.people.length - 1;
        return <VStack key={index} align="start" gap={8} w="full"><Heading size="2xl">Instructor</Heading><Stack align="center" direction="row" gap={2}><Avatar.Root size="md"><Avatar.Fallback /><Avatar.Image src={expert.avatar.url} /></Avatar.Root><Heading fontWeight={600} size="lg">{expert.firstName} {expert.lastName}</Heading></Stack><Box><MarkdownRenderer preset="edu">{expert.shortBio}</MarkdownRenderer></Box><Separator hidden={isLast} /></VStack>;
      });
      $[36] = activeVideo?.people;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== t16) {
      t17 = <VStack alignItems="left" flex={1} gap={8} w="full">{t16}</VStack>;
      $[38] = t16;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    let t19;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Separator borderColor="backgroundTertiary" />;
      t19 = <Separator borderColor="backgroundTertiary" />;
      $[40] = t18;
      $[41] = t19;
    } else {
      t18 = $[40];
      t19 = $[41];
    }
    let t20;
    if ($[42] !== activeVideo?.transcript) {
      t20 = activeVideo?.transcript && <VStack align="flex-start" flex={2} gap={10} maxWidth="container.md" width="full"><Heading size="2xl">Transcript</Heading><MarkdownRenderer preset="transcript">{activeVideo?.transcript}</MarkdownRenderer></VStack>;
      $[42] = activeVideo?.transcript;
      $[43] = t20;
    } else {
      t20 = $[43];
    }
    let t21;
    if ($[44] !== t17 || $[45] !== t20) {
      t21 = <Stack align="flex-start" direction="column" gap={10} maxWidth="container.lg">{t17}{t18}{t19}{t20}</Stack>;
      $[44] = t17;
      $[45] = t20;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    let t22;
    if ($[47] !== t15 || $[48] !== t21) {
      t22 = <Flex justify="center" padding={t15} width="full">{t21}</Flex>;
      $[47] = t15;
      $[48] = t21;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== t14 || $[51] !== t22) {
      t23 = <VStack align="center" gap={0} width="full">{t14}{t22}</VStack>;
      $[50] = t14;
      $[51] = t22;
      $[52] = t23;
    } else {
      t23 = $[52];
    }
    return t23;
  }
  if (videoListQuery.error) {
    let t12;
    if ($[53] !== videoListQuery.error) {
      t12 = videoListQuery.error.toString();
      $[53] = videoListQuery.error;
      $[54] = t12;
    } else {
      t12 = $[54];
    }
    let t13;
    if ($[55] !== t12) {
      t13 = <Center padding="2em">{t12}</Center>;
      $[55] = t12;
      $[56] = t13;
    } else {
      t13 = $[56];
    }
    return t13;
  }
  if (!videoListQuery.loading && !videoListQuery.data) {
    let t12;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <EducationErrorState ctaHref="/videos" ctaLabel="Explore all videos." headingText="Video not found." />;
      $[57] = t12;
    } else {
      t12 = $[57];
    }
    return t12;
  }
  let t12;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Center padding="2em"><Spinner /></Center>;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  return t12;
};
VideoLessonDetail.queries = {
  videoList: () => gql`
    query ($videoListSlug: String) {
      videoList(where: { slug: $videoListSlug }) {
        id
        featuredImage {
          id
          url(transformation: { image: { resize: { fit: max, width: 1000 } } })
        }
        slug
        title
        shortDescription
        people {
          id
          firstName
          lastName
          email
        }
        videos {
          id
          slug
          title
          featuredImage {
            id
            url
          }
          title
          subtitle
          transcript
          people {
            id
            firstName
            lastName
            shortBio
            avatar {
              url
            }
          }
          shortDescription
          longDescription
          videoUrl
        }
      }
    }
  `
};